import { useState, useCallback } from "react";
import { Bell, Loader2, X, Trash2 } from "lucide-react";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSocket } from "@/hooks/useSocket";
import { useSocketEvent } from "@/hooks/useSocketEvent";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { Notification } from "@/shared/types/common.types";

const PAGE_SIZE = 10;

interface NotificationsResponse {
  data: Notification[];
  totalCount: number;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const socket = useSocket();

  // Unread count query
  const { data: unreadData, isLoading: isUnreadLoading } = useQuery<{ count: number }>({
    queryKey: ["/notifications/unread-count"],
  });
  const unreadCount = unreadData?.count ?? 0;

  // Full notification list (only fetched when popover is open)
  const {
    data: notificationsData,
    isFetching,
    isLoading: isListLoading,
  } = useQuery<NotificationsResponse>({
    queryKey: ["/notifications", visibleCount],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/notifications?limit=${visibleCount}&offset=0`
      );
      return res.json();
    },
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const notifications = notificationsData?.data ?? [];
  const totalCount = notificationsData?.totalCount ?? 0;

  // Data has been loaded at least once (we have a response from the server)
  const hasLoaded = !!notificationsData;

  // First load = popover open + no data yet
  const isFirstLoad = open && isListLoading && !hasLoaded;

  // Server tells us exactly whether more exist
  const hasMore = totalCount > notifications.length;

  // Socket: real-time new notifications
  useSocketEvent<Notification>(
    "notification:new",
    () => {
      queryClient.invalidateQueries({
        queryKey: ["/notifications/unread-count"],
      });
      if (open) {
        queryClient.invalidateQueries({ queryKey: ["/notifications"] });
      }
    },
    [open],
    socket
  );

  // Mutations with optimistic updates
  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("PATCH", `/notifications/${id}/read`);
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["/notifications"] });
      await queryClient.cancelQueries({ queryKey: ["/notifications/unread-count"] });

      const prevNotifications = queryClient.getQueriesData<NotificationsResponse>({ queryKey: ["/notifications"] });
      const prevUnread = queryClient.getQueryData<{ count: number }>(["/notifications/unread-count"]);

      queryClient.setQueriesData<NotificationsResponse>(
        { queryKey: ["/notifications"] },
        (old) => old ? {
          ...old,
          data: old.data.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
        } : old
      );

      if (prevUnread && prevUnread.count > 0) {
        queryClient.setQueryData(["/notifications/unread-count"], {
          count: prevUnread.count - 1,
        });
      }

      return { prevNotifications, prevUnread };
    },
    onError: (_err, _id, context) => {
      if (context?.prevNotifications) {
        for (const [key, data] of context.prevNotifications) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.prevUnread) {
        queryClient.setQueryData(["/notifications/unread-count"], context.prevUnread);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/notifications/unread-count"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/notifications/mark-all-read");
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["/notifications"] });
      await queryClient.cancelQueries({ queryKey: ["/notifications/unread-count"] });

      const prevNotifications = queryClient.getQueriesData<NotificationsResponse>({ queryKey: ["/notifications"] });
      const prevUnread = queryClient.getQueryData<{ count: number }>(["/notifications/unread-count"]);

      queryClient.setQueriesData<NotificationsResponse>(
        { queryKey: ["/notifications"] },
        (old) => old ? {
          ...old,
          data: old.data.map((n) => ({ ...n, isRead: true })),
        } : old
      );
      queryClient.setQueryData(["/notifications/unread-count"], { count: 0 });

      return { prevNotifications, prevUnread };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevNotifications) {
        for (const [key, data] of context.prevNotifications) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.prevUnread) {
        queryClient.setQueryData(["/notifications/unread-count"], context.prevUnread);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/notifications/unread-count"] });
    },
  });

  // Delete single notification (optimistic)
  const deleteNotificationMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/notifications/${id}`);
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["/notifications"] });
      await queryClient.cancelQueries({ queryKey: ["/notifications/unread-count"] });

      const prevNotifications = queryClient.getQueriesData<NotificationsResponse>({ queryKey: ["/notifications"] });
      const prevUnread = queryClient.getQueryData<{ count: number }>(["/notifications/unread-count"]);

      let wasUnread = false;
      queryClient.setQueriesData<NotificationsResponse>(
        { queryKey: ["/notifications"] },
        (old) => {
          if (!old) return old;
          const target = old.data.find((n) => n.id === id);
          if (target && !target.isRead) wasUnread = true;
          return {
            data: old.data.filter((n) => n.id !== id),
            totalCount: old.totalCount - 1,
          };
        }
      );

      if (wasUnread && prevUnread && prevUnread.count > 0) {
        queryClient.setQueryData(["/notifications/unread-count"], {
          count: prevUnread.count - 1,
        });
      }

      return { prevNotifications, prevUnread };
    },
    onError: (_err, _id, context) => {
      if (context?.prevNotifications) {
        for (const [key, data] of context.prevNotifications) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.prevUnread) {
        queryClient.setQueryData(["/notifications/unread-count"], context.prevUnread);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/notifications/unread-count"] });
    },
  });

  // Clear all notifications (optimistic)
  const clearAllMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", "/notifications");
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["/notifications"] });
      await queryClient.cancelQueries({ queryKey: ["/notifications/unread-count"] });

      const prevNotifications = queryClient.getQueriesData<NotificationsResponse>({ queryKey: ["/notifications"] });
      const prevUnread = queryClient.getQueryData<{ count: number }>(["/notifications/unread-count"]);

      queryClient.setQueriesData<NotificationsResponse>(
        { queryKey: ["/notifications"] },
        () => ({ data: [], totalCount: 0 })
      );
      queryClient.setQueryData(["/notifications/unread-count"], { count: 0 });

      return { prevNotifications, prevUnread };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevNotifications) {
        for (const [key, data] of context.prevNotifications) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.prevUnread) {
        queryClient.setQueryData(["/notifications/unread-count"], context.prevUnread);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/notifications/unread-count"] });
    },
  });

  // Handlers
  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) {
        markAsReadMutation.mutate(notification.id);
      }
      if (notification.link) {
        const url = new URL(notification.link, window.location.origin);
        const highlightId = url.searchParams.get("highlight");

        setLocation(notification.link);

        if (highlightId) {
          window.dispatchEvent(
            new CustomEvent("highlight-submission", {
              detail: { submissionId: highlightId },
            })
          );
        }
      }
      setOpen(false);
    },
    [markAsReadMutation, setLocation]
  );

  const handleDismissNotification = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      deleteNotificationMutation.mutate(id);
    },
    [deleteNotificationMutation]
  );

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  }, []);

  // Reset visibleCount when popover closes for a clean state next time
  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setVisibleCount(PAGE_SIZE);
    }
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          disabled={isUnreadLoading}
          data-testid="button-notifications"
        >
          {isUnreadLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Bell className="h-5 w-5" />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="!w-[calc(100vw-1rem)] sm:!w-96 p-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h4 className="text-sm font-semibold">Notifications</h4>
          <div className="flex items-center gap-1">
            {hasLoaded && unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-auto py-1"
                onClick={() => markAllReadMutation.mutate()}
                disabled={markAllReadMutation.isPending}
              >
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-auto py-1 text-destructive hover:text-destructive"
                onClick={() => clearAllMutation.mutate()}
                disabled={clearAllMutation.isPending}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Notification list */}
        <div className="max-h-[400px] overflow-y-auto">
          {isFirstLoad ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-2">Loading...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Bell className="h-8 w-8 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                No notifications yet
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  className={cn(
                    "group relative flex items-start",
                    !n.isRead && "border-l-2 border-l-primary bg-primary/5"
                  )}
                >
                  <button
                    onClick={() => handleNotificationClick(n)}
                    className="flex-1 text-left px-4 py-3 hover:bg-muted/50 transition-colors pr-8"
                  >
                    <p className="text-sm font-medium leading-tight">{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {n.message}
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {n.createdAt ? formatRelativeTime(new Date(n.createdAt)) : ""}
                    </p>
                  </button>
                  <button
                    onClick={(e) => handleDismissNotification(e, n.id)}
                    className="absolute right-2 top-2 p-1 rounded-sm opacity-0 group-hover:opacity-100 hover:bg-muted transition-all"
                    title="Dismiss"
                  >
                    <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Show more button - server totalCount drives visibility */}
        {hasMore && (
          <div className="border-t px-4 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={handleLoadMore}
              disabled={isFetching}
            >
              {isFetching ? "Loading..." : "Show more"}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
